<template>
  <div class="drawer-filter product-filter-drawer offer-filter-drawer">
    <a class="filter-trigger" :title="$t('offers.filter.title')" @click="isOpen = !isOpen">
      <i class="fa fa-filter fa-2x"></i>
      <span v-if="activeFilterCount > 0" class="filter-badge">{{ activeFilterCount }}</span>
    </a>
    <div v-if="isOpen" class="filter-overlay" @click="isOpen = false"></div>
    <div class="filter-panel" :class="{ open: isOpen }">
      <div class="filter-panel-header">
        <span>{{ $t("offers.filter.title") }}<template v-if="activeFilterCount > 0"> ({{ activeFilterCount }})</template></span>
        <md-button class="md-simple md-just-icon md-round" @click="isOpen = false"><md-icon>clear</md-icon></md-button>
      </div>
      <div class="filter-panel-body">
        <div class="filter-section">
          <div class="filter-section-title">{{ $t("offers.filter.sectionPresets") }}</div>
          <div class="offer-filter-presets">
            <md-checkbox v-for="p in negativePresets" :key="p.key" :model="isNegative(p.key)" @change="toggleNegative(p.key)">{{ p.label }}</md-checkbox>
          </div>
          <div class="offer-filter-presets">
            <md-radio v-for="f in floorPresets" :key="f" v-model="priceBelow" :value="f">{{ $t("offers.filter.priceBelow", { floor: f.toUpperCase() }) }}</md-radio>
            <md-radio v-model="priceBelow" value="">{{ $t("offers.filter.priceBelowNone") }}</md-radio>
          </div>
        </div>

        <div class="filter-section">
          <div class="filter-section-title">{{ $t("offers.filter.sectionKpi") }}</div>
          <div v-for="k in kpiFields" :key="k.key" class="filter-kpi">
            <span class="filter-kpi-label">{{ k.label }}</span>
            <select v-model="kpi[k.key].op" class="filter-kpi-op">
              <option value="">{{ $t("products.filter.opNone") }}</option>
              <option value="gt">&gt;</option>
              <option value="lt">&lt;</option>
              <option value="eq">=</option>
            </select>
            <input
              v-model="kpi[k.key].value"
              type="number"
              class="filter-kpi-value"
              :step="1 / Math.pow(10, k.decimals)"
              :disabled="!kpi[k.key].op"
              :class="{ 'filter-kpi-value--invalid': !kpiValid(k) }"
            />
            <span class="filter-kpi-unit">{{ $t("products.filter.unit." + k.unit) }}</span>
          </div>
        </div>
      </div>
      <div class="filter-panel-footer">
        <md-button class="md-secondary" @click="clearFilters">{{ $t("shippingPlan.clear") }}</md-button>
        <md-button class="md-success" :disabled="!allKpiValid" @click="applyFilters">{{ $t("shippingPlan.apply") }}</md-button>
      </div>
    </div>
  </div>
</template>

<script>
import { DrawerScrollLockMixin } from "@/mixins/drawerScrollLock";

// Schluessel wie OfferKpiFields in der API; decimals = erlaubte Nachkommastellen im Eingabefeld
const KPI_KEYS = [
  { key: "purchasePriceEur", label: "ek", decimals: 2, unit: "eur" },
  { key: "bep", label: "BEP", decimals: 2, unit: "eur" },
  { key: "grossProfit", label: "grossProfit", decimals: 2, unit: "eur" },
  { key: "grossProfitPercent", label: "grossProfit", decimals: 1, unit: "percent" },
  { key: "db1", label: "DB1", decimals: 2, unit: "eur" },
  { key: "db1Percent", label: "DB1", decimals: 1, unit: "percent" },
  { key: "db2", label: "DB2", decimals: 2, unit: "eur" },
  { key: "db2Percent", label: "DB2", decimals: 1, unit: "percent" },
  { key: "db3", label: "DB3", decimals: 2, unit: "eur" },
  { key: "db3Percent", label: "DB3", decimals: 1, unit: "percent" },
  { key: "saleGross", label: "saleGross", decimals: 2, unit: "eur" },
  { key: "saleNet", label: "saleNet", decimals: 2, unit: "eur" },
  { key: "kpu", label: "KPU", decimals: 2, unit: "eur" },
  { key: "opu", label: "OPU", decimals: 2, unit: "eur" },
  { key: "lpu", label: "LPU", decimals: 2, unit: "eur" },
];

const emptyKpi = () => Object.fromEntries(KPI_KEYS.map((k) => [k.key, { op: "", value: "" }]));

export default {
  name: "OfferFilterDrawer",
  mixins: [DrawerScrollLockMixin],
  data() {
    return { isOpen: false, kpi: emptyKpi(), priceBelow: "", floorPresets: ["kpu", "opu", "lpu"] };
  },
  computed: {
    kpiFields() {
      const named = { ek: this.$t("offers.filter.ek"), grossProfit: this.$t("offers.marginGrossProfit"), saleGross: this.$t("offerCalculation.saleGross"), saleNet: this.$t("offerCalculation.saleNet") };
      // Kennzahlen, die es in € und % gibt, tragen die Einheit in Klammern (Trello KWDTD2pU, Update d)
      const paired = (label) => KPI_KEYS.filter((k) => k.label === label).length > 1;
      return KPI_KEYS.map((k) => {
        const label = named[k.label] || k.label;
        return { ...k, label: paired(k.label) ? `${label} (${this.$t("products.filter.unit." + k.unit)})` : label };
      });
    },
    negativePresets() {
      return [
        { key: "grossProfit", label: this.$t("offers.filter.negative", { name: this.$t("offers.marginGrossProfit") }) },
        { key: "db1", label: this.$t("offers.filter.negative", { name: "DB1" }) },
        { key: "db2", label: this.$t("offers.filter.negative", { name: "DB2" }) },
        { key: "db3", label: this.$t("offers.filter.negative", { name: "DB3" }) },
      ];
    },
    activeKpis() {
      return KPI_KEYS.filter((k) => this.kpi[k.key].op && this.kpi[k.key].value !== "" && this.kpi[k.key].value != null);
    },
    activeFilterCount() {
      return this.activeKpis.length + (this.priceBelow ? 1 : 0);
    },
    allKpiValid() {
      return KPI_KEYS.every((k) => this.kpiValid(k));
    },
  },
  mounted() {
    document.addEventListener("click", this.onClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.onClickOutside);
  },
  methods: {
    isNegative(key) {
      const entry = this.kpi[key];
      return entry.op === "lt" && entry.value !== "" && Number(entry.value) === 0;
    },
    // Standardfilter "negativ" = Kennzahl in Euro kleiner 0; ein zweiter Klick nimmt ihn wieder heraus
    toggleNegative(key) {
      this.kpi[key] = this.isNegative(key) ? { op: "", value: "" } : { op: "lt", value: "0" };
    },
    kpiValid(k) {
      const entry = this.kpi[k.key];
      if (!entry.op || entry.value === "" || entry.value == null) return true;
      const text = String(entry.value);
      if (!Number.isFinite(Number(text))) return false;
      const decimals = text.includes(".") ? text.split(".")[1].length : 0;
      return decimals <= k.decimals;
    },
    clearFilters() {
      this.kpi = emptyKpi();
      this.priceBelow = "";
    },
    applyFilters() {
      const kpiFilters = this.activeKpis.map((k) => `${k.key}:${this.kpi[k.key].op}:${Number(this.kpi[k.key].value)}`).join(",");
      this.$emit("apply-filters", { kpiFilters: kpiFilters || null, priceBelow: this.priceBelow || null });
      this.isOpen = false;
    },
    onClickOutside(e) {
      const inMenu = e.target.closest && e.target.closest(".md-menu-content, .vs__dropdown-menu");
      if (this.isOpen && !inMenu && document.body.contains(e.target) && !this.$el.contains(e.target)) {
        this.isOpen = false;
      }
    },
  },
};
</script>

<style>
.offer-filter-drawer .offer-filter-presets {
  display: flex;
  flex-wrap: wrap;
  column-gap: 18px;
}
.offer-filter-drawer .offer-filter-presets .md-checkbox,
.offer-filter-drawer .offer-filter-presets .md-radio {
  margin: 6px 0;
}
</style>
